import { useQuery } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../stores/authStore'
import { parseCsvToWorkouts, validateCsvFormat } from '../../lib/csv-parser'
import { WorkoutsResponse } from '../../types'
import { queryKeys } from '../queryKeys'
import { useUserCSVUpload } from './useCSVStorage'

// Type for performance.memory API
interface PerformanceMemory {
  usedJSHeapSize: number
  totalJSHeapSize: number
  jsHeapSizeLimit: number
}

// Extended Performance interface
interface ExtendedPerformance extends Performance {
  memory?: PerformanceMemory
}

export function useCSVWorkoutData() {
  const { user } = useAuth()
  const { data: csvUpload } = useUserCSVUpload()

  return useQuery({
    queryKey: queryKeys.csvWorkoutData(user?.id || '', csvUpload?.uploadDate || ''),
    queryFn: async (): Promise<WorkoutsResponse> => {
      if (!user) {
        throw new Error('Authentication required to load CSV data')
      }

      if (!csvUpload) {
        throw new Error('No CSV file found. Please upload a CSV file in Settings.')
      }

      try {
        // Download CSV file from storage
        const { data: csvBlob, error: downloadError } = await supabase.storage
          .from('user-csv-files')
          .download(user.id)

        if (downloadError) {
          console.error('Failed to download CSV:', downloadError)

          // Provide more specific error messages
          if (downloadError.message.includes('not found')) {
            throw new Error('CSV file not found. Please re-upload your CSV file.')
          }

          if (downloadError.message.includes('permission')) {
            throw new Error('Access denied. Please check your permissions.')
          }

          throw new Error(`Failed to download CSV: ${downloadError.message}`)
        }

        if (!csvBlob) {
          throw new Error('CSV file is empty or corrupted. Please re-upload your file.')
        }

        // Convert blob to text
        const csvContent = await csvBlob.text()

        if (!csvContent.trim()) {
          throw new Error('CSV file appears to be empty. Please check your file and re-upload.')
        }

        // Validate CSV format before parsing
        const validation = validateCsvFormat(csvContent)
        if (!validation.isValid) {
          throw new Error(`Invalid CSV format: ${validation.error}`)
        }

        // Parse CSV to workouts
        try {
          return parseCsvToWorkouts(csvContent)
        } catch (parseError) {
          console.error('CSV parsing failed:', parseError)

          if (parseError instanceof Error) {
            // Handle specific parsing errors
            if (parseError.message.includes('No valid workout data')) {
              throw new Error(
                'No valid workout data found in CSV. Please check your export includes workouts.'
              )
            }

            if (parseError.message.includes('Missing required column')) {
              throw new Error(
                `CSV format error: ${parseError.message}. Please export from Hevy with all columns.`
              )
            }

            throw new Error(`CSV parsing failed: ${parseError.message}`)
          }

          throw new Error('Failed to parse CSV file. Please check the file format.')
        }
      } catch (error) {
        // Log error for debugging
        console.error('CSV workout data error:', {
          error,
          userId: user?.id,
          csvUpload,
          timestamp: new Date().toISOString(),
          browser: navigator.userAgent,
          memoryUsage: (() => {
            const extPerf = performance as ExtendedPerformance
            return extPerf.memory
              ? {
                  usedJSHeapSize: extPerf.memory.usedJSHeapSize,
                  totalJSHeapSize: extPerf.memory.totalJSHeapSize,
                  jsHeapSizeLimit: extPerf.memory.jsHeapSizeLimit
                }
              : 'unavailable'
          })()
        })

        // Re-throw with user-friendly message
        if (error instanceof Error) {
          throw error
        }

        throw new Error(
          `Failed to load CSV workout data from "${csvUpload?.fileName || 'unknown file'}". Please try re-uploading your file.\n\n🔍 Debug info:\n- Upload date: ${csvUpload?.uploadDate ? new Date(csvUpload.uploadDate).toLocaleString() : 'unknown'}\n- File size: ${csvUpload?.fileSize || 'unknown'} bytes\n- Browser: ${navigator.userAgent}\n- Timestamp: ${new Date().toLocaleString()}`
        )
      }
    },
    enabled: !!user && !!csvUpload,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      // Log retry attempts
      // console.log(`CSV workout data retry attempt ${failureCount + 1}:`, {
      //   error: error instanceof Error ? error.message : 'Unknown error',
      //   userId: user?.id,
      //   csvUpload,
      //   timestamp: new Date().toISOString()
      // })

      // Don't retry on user errors (validation, format issues, etc.)
      if (error instanceof Error) {
        const message = error.message.toLowerCase()
        if (
          message.includes('format') ||
          message.includes('empty') ||
          message.includes('invalid') ||
          message.includes('not found') ||
          message.includes('permission') ||
          message.includes('corrupted')
        ) {
          return false
        }
      }

      // Retry up to 2 times for network/download errors
      return failureCount < 2
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000)
  })
}
